import Image from '@atlaskit/image';
import type { SmartPrompt } from '@atlassian/conversation-assistant-blank-object-experience/types';
import React, { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl-next';

import {
  DATABASE_SMART_PROMPT_ENTRIES,
  type DatabaseSmartPromptEntry,
} from './database-smart-prompt-entries';

// Mirrors the job-title-to-role mapping in createWithRovoSmartPrompts.tsx
// Source: confluence/next/packages/blank-page-quick-actions/src/createWithRovoSmartPrompts.tsx
const keywordToCategoryMap: ReadonlyArray<readonly [string, string]> = [
  ['engineer', 'engineering'],
  ['developer', 'engineering'],
  ['programmer', 'engineering'],
  ['product', 'productManagement'],
  ['pm', 'productManagement'],
  ['marketing', 'marketing'],
  ['brand', 'marketing'],
  ['campaign', 'marketing'],
  ['sales', 'sales'],
  ['account', 'sales'],
  ['program', 'programManagement'],
  ['project manager', 'programManagement'],
];

const getJobCategoryForTitle = (jobTitle: string | undefined): string => {
  if (!jobTitle) {
    return 'other';
  }
  const lowerTitle = jobTitle.toLowerCase();
  for (const [keyword, category] of keywordToCategoryMap) {
    if (lowerTitle.includes(keyword)) {
      return category;
    }
  }
  return 'other';
};

const createIconComponent = (iconUrl: string): SmartPrompt['icon'] => {
  const SmartPromptIcon = ({ size }: { size?: string; color?: string } = {}) => {
    const dimension = size === 'small' ? 20 : 100;
    return React.createElement(Image, {
      src: iconUrl,
      alt: '',
      width: dimension,
      height: dimension,
    });
  };
  SmartPromptIcon.displayName = 'SmartPromptIcon';
  return SmartPromptIcon;
};

const toSmartPrompt = (
  entry: DatabaseSmartPromptEntry,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt => ({
  name: entry.name,
  text: entry.prompt,
  label: formatMessage(entry.templateName),
  icon: createIconComponent(entry.icon),
  templateSnapshot: { default: '', dark: '' },
  prompt: entry.prompt,
  templateName: entry.templateName,
});

const getDatabaseSmartPrompts = (
  jobTitle: string | undefined,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt[] => {
  const jobCategory = getJobCategoryForTitle(jobTitle);

  const primaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) => e.userRole === jobCategory && e.cardLevel === 'primary'
  );

  const secondaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) =>
      (e.userRole === jobCategory && e.cardLevel === 'secondary') ||
      (e.userRole === 'other' && (jobCategory !== 'other' || e.cardLevel !== 'primary'))
  );

  return [...primaryEntries, ...secondaryEntries]
    .slice(0, 8)
    .map((entry) => toSmartPrompt(entry, formatMessage));
};

export const useDatabaseSmartPrompts = (jobTitle?: string): SmartPrompt[] => {
  const { formatMessage } = useIntl();
  return useMemo(
    () => getDatabaseSmartPrompts(jobTitle, formatMessage),
    [jobTitle, formatMessage]
  );
};
